import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AppShell, PageHeader } from '@/components/AppShell';
import { VehicleTrustBadge } from '@/components/VehicleTrust';
import { api } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { ArrowLeft, Search, Loader2, ShieldQuestion, Info } from 'lucide-react';

export default function VehicleCheck() {
  const navigate = useNavigate();
  const t = useT();
  const [plate, setPlate] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [searched, setSearched] = useState(false);

  const lookup = async (e) => {
    e?.preventDefault();
    const q = plate.trim();
    if (!q) return;
    setBusy(true);
    setSearched(false);
    try {
      const res = await api.getVehicle(q);
      setResult(res);
      setSearched(true);
    } catch { toast.error(t('vt_lookup_fail')); }
    finally { setBusy(false); }
  };

  return (
    <AppShell nav={false}>
      <button
        onClick={() => navigate('/app')}
        className="inline-flex items-center gap-1 text-sm font-semibold mb-3"
        style={{ color: 'var(--primary)' }}
        data-testid="vehicle-check-back"
      >
        <ArrowLeft size={18} /> {t('back')}
      </button>

      <PageHeader title={t('vt_check_title')} subtitle={t('vt_check_sub')} />

      <form onSubmit={lookup} className="rounded-2xl p-3 border mb-4 flex items-center gap-2" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
        <Search size={18} style={{ color: 'var(--primary)' }} />
        <input
          value={plate}
          onChange={(e) => setPlate(e.target.value)}
          placeholder={t('vt_check_ph')}
          className="flex-1 bg-transparent outline-none text-sm uppercase"
          style={{ color: 'var(--text)' }}
          data-testid="vehicle-check-input"
          autoFocus
        />
        <button
          type="submit"
          disabled={busy || !plate.trim()}
          className="rounded-lg px-4 py-2 text-xs font-semibold disabled:opacity-50 inline-flex items-center gap-1.5"
          style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}
          data-testid="vehicle-check-submit"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
          {t('vt_check_btn')}
        </button>
      </form>

      {searched && result && (
        result.known ? (
          <div className="rounded-2xl p-4 border mb-4" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid="vehicle-check-result">
            <div className="text-xs font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>{t('vt_result_for')}</div>
            <div className="text-xl font-extrabold mb-3 tracking-wide" style={{ color: 'var(--text)' }}>{result.plate || result.plate_normalized}</div>
            <VehicleTrustBadge trust={result} />
          </div>
        ) : (
          <div className="rounded-2xl p-4 border mb-4 flex items-start gap-3" style={{ background: 'var(--surface-alt)', borderColor: 'var(--border)' }} data-testid="vehicle-check-unknown">
            <ShieldQuestion size={22} style={{ color: 'var(--text-muted)' }} />
            <div>
              <p className="font-bold text-sm" style={{ color: 'var(--text)' }}>{t('vt_unknown_t')}</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{t('vt_unknown_b')}</p>
            </div>
          </div>
        )
      )}

      <div className="rounded-2xl p-3 border flex items-start gap-2" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
        <Info size={18} style={{ color: 'var(--primary)' }} />
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('vt_disclaimer')}</p>
      </div>
    </AppShell>
  );
}
